import { Pencil, Plus, Star, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import {
  useContactPersons,
  useDeleteContactPerson,
  useSetDefaultContactPerson,
} from '@/api/domains/contact-persons';
import type { ContactPerson } from '@/api/types/pickups';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toApiProblem, toastProblem } from '@/lib/errors';

import { ContactPersonFormDialog } from '../components/ContactPersonFormDialog';

type Props = {
  dirtyRef: (dirty: boolean) => void;
};

export function ContactPersonsTab({ dirtyRef }: Props) {
  const { t } = useTranslation('settings');
  const tCommon = useTranslation('common').t;
  const contacts = useContactPersons();
  const remove = useDeleteContactPerson();
  const setDefault = useSetDefaultContactPerson();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ContactPerson | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<ContactPerson | null>(null);

  useEffect(() => dirtyRef(false), [dirtyRef]);

  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };

  const openEdit = (contact: ContactPerson) => {
    setEditing(contact);
    setFormOpen(true);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;
    remove.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast.success(t('contactPersons.deleted'));
        setDeleteTarget(null);
      },
      onError: (error) => toastProblem(toApiProblem(error)),
    });
  };

  const rows = contacts.data ?? [];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <h3 className="text-xl font-medium tracking-[-0.6px] text-black">
            {t('contactPersons.title')}
          </h3>
          <p className="text-base text-muted-foreground">{t('contactPersons.description')}</p>
        </div>
        <Button type="button" onClick={openCreate}>
          <Plus className="size-4" />
          {t('contactPersons.add')}
        </Button>
      </div>

      <div className="overflow-x-auto rounded-xl border border-[#e5e5e5]">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('contactPersons.fields.name')}</TableHead>
              <TableHead>{t('contactPersons.fields.email')}</TableHead>
              <TableHead>{t('contactPersons.fields.phone')}</TableHead>
              <TableHead className="w-44 text-right">{tCommon('actions.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {contacts.isLoading ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                  {tCommon('states.loading')}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                  {t('contactPersons.empty')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((contact) => (
                <TableRow key={contact.id}>
                  <TableCell className="font-medium text-black">
                    <div className="flex items-center gap-2">
                      {contact.full_name}
                      {contact.is_default ? (
                        <Badge variant="secondary">{t('contactPersons.default')}</Badge>
                      ) : null}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground">{contact.email}</TableCell>
                  <TableCell className="text-muted-foreground">
                    {`${contact.phone_prefix} ${contact.phone_number}`}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      {!contact.is_default ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          title={t('contactPersons.setDefault')}
                          onClick={() => setDefault.mutate(contact.id)}
                        >
                          <Star className="size-4" />
                        </Button>
                      ) : null}
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={tCommon('actions.edit')}
                        onClick={() => openEdit(contact)}
                      >
                        <Pencil className="size-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        title={tCommon('actions.delete')}
                        onClick={() => setDeleteTarget(contact)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <ContactPersonFormDialog open={formOpen} onOpenChange={setFormOpen} contact={editing} />

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('contactPersons.deleteTitle')}</AlertDialogTitle>
            <AlertDialogDescription>{t('contactPersons.deleteDescription')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{tCommon('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} disabled={remove.isPending}>
              {tCommon('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
